import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CloudIcon from '@mui/icons-material/CloudOutlined';
import CoverageIcon from '@mui/icons-material/Texture';
import InstrumentsIcon from '@mui/icons-material/Sensors';
import PartialRectIcon from '@mui/icons-material/GpsOff';
import PixelResolutionIcon from '@mui/icons-material/CenterFocusStrong';
import PropTypes from 'prop-types';
import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';

export const ItemIndicator = ({tooltip, useMonospace = false, ...props}) => (
  <Tooltip title={tooltip || ''}>
    <Chip
      color="default"
      sx={{fontFamily: useMonospace ? 'monospace' : undefined}}
      {...props}
    />
  </Tooltip>
);

ItemIndicator.propTypes = {
  tooltip: PropTypes.string,
  useMonospace: PropTypes.bool,
};

export const CloudCoverIndicator = ({cloudPercent, ...props}) => {
  const {t} = useTranslation();

  return (
    <ItemIndicator
      tooltip={`${t('common:cloudCover.label')}: ${t(
        'common:cloudCover.shortMessage',
        {cloudPercent}
      )}`}
      icon={<CloudIcon />}
      label={t('common:cloudCover.shortMessage', {cloudPercent})}
      useMonospace
      data-qe="item-cloud-cover"
      {...props}
    />
  );
};

CloudCoverIndicator.propTypes = {
  cloudPercent: PropTypes.number,
};

export const AreaCoverageIndicator = ({coverage, ...props}) => {
  const {t} = useTranslation();
  return (
    <ItemIndicator
      tooltip={t('common:areaCoverage.label')}
      icon={<CoverageIcon />}
      label={t(
        'common:areaCoverage.shortMessage',
        '(0) [<1%]; (1-100) [{{count}}%];',
        {
          postProcess: 'interval',
          count: coverage,
        }
      )}
      useMonospace
      data-qe="area-coverage"
      {...props}
    />
  );
};

AreaCoverageIndicator.propTypes = {
  coverage: PropTypes.number,
};

export const PixelResolutionIndicator = ({resolution, ...props}) => {
  const {t} = useTranslation();

  return (
    <ItemIndicator
      tooltip={t('common:pixelResolution.label')}
      icon={<PixelResolutionIcon />}
      label={t('common:pixelResolution.shortMessage', {resolution})}
      useMonospace
      data-qe="item-pixel-resolution"
      {...props}
    />
  );
};

PixelResolutionIndicator.propTypes = {
  resolution: PropTypes.number,
};

export const PartialRectIndicator = props => {
  const {t} = useTranslation();
  return (
    <Tooltip
      title={t(
        'common:partiallyRectified',
        'Contains partially rectified imagery'
      )}
    >
      <Chip
        margin="normal"
        icon={<PartialRectIcon />}
        label={t('common:unrectifiedAssets')}
        data-qe="partial-rectification-indicator"
        {...props}
      />
    </Tooltip>
  );
};

export const IndicatorsContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  '& > *': {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
}));

export const InstrumentsIndicator = props => {
  const {t} = useTranslation();
  return (
    <ItemIndicator
      icon={<InstrumentsIcon />}
      data-qe="instrument-type-indicator"
      tooltip={t('common:tooltipInstrumentType')}
      {...props}
    />
  );
};
